import { type SalesCampaignProduct } from "~/domains/salesCampaign/types/SalesCampaign";
import { type HousingProductType, type HousingType, type ProductMarketType } from "~/domains/product/types/ProductData";

export enum ImageType {
  primary = "PRIMARY",
  gallery = "GALLERY",
  tech = "TECH",
}

export enum ExternalBrand {
  Ewellix = "EWELLIX",
}

export enum ImageFormat {
  thumbnail = "thumbnail",
  product = "product",
  zoom = "zoom",
  superZoom = "superZoom",
}

export interface ImageDataDTO {
  imageType: ImageType;
  url: string;
  altText: string;
  format: ImageFormat;
  commentsFootnotes?: FootnoteDTO[];
}

export interface ProductLinearInfoDTO {
  designation: string;
  sapMaterialNumber: string;
  aliasesString: string;
  images: ImageDataDTO[];
  leadTime: number;
  guideways: string;
  carriages: string;
  configId: string;
}

export interface FootnoteDTO {
  currentFootnotesURL: string;
  fallbackFootnotesURL: string;
}

export type ExternalBrandId = "FAG" | "INA" | "SCHAEFFLER" | "EWELLIX";

export interface ProductDataDTO {
  code: string;

  preferred?: boolean;
  price: {
    preferred?: boolean;
  } | null;

  manufacturerLogoUrl?: string;
  manufacturerBrandId?: ExternalBrandId;
  brandLogoUrl?: string;
  images: ImageDataDTO[] | null;
  url: string;

  designation: string;
  oldDesignation: string[] | null;
  marketDesignation: string;
  isMMDAssistantAvailableForProduct: boolean;
  isCMNSearchMatch: boolean;
  sapMaterialNumber: string;
  shortDescription: string;
  description: string;
  /** Collection of 13 digit level customer material numbers */
  aliases: string[] | null;
  /** Collection of 15 digit level customer material numbers */
  packageAliases: Dictionary<string[]> | null;
  productLinearInfo?: ProductLinearInfoDTO | null;
  mass: number | null;
  massUnit: string | null;
  massImperial: number | null;
  massImperialUnit: string | null;
  baseTypeNorm: string | null;

  productLineId: string | null;
  productAllCategories: string[] | null;
  isOptime: boolean;
  quantityVisible: boolean;
  isPMA: boolean;
  type: ProductMarketType | null;
  fifteenDigit?: FifteenDigitDTOEntry[];
  gpsdGroup: string | null;
  showBasicFrequencies: boolean | null;

  succeedingProduct?: ProductDataDTO;
  alternativeProducts?: ProductDataDTO[];
  predecessorProducts?: ProductDataDTO[];

  classifications?: ClassificationDTO[];
  technicalDetails: TechnicalDetailsDataDTO[] | null;

  housingVariantCode?: string; // The productCode for housing KIT
  housingType?: { code: HousingType };
  optionalParts?: HousingDTO[];
  includedParts?: HousingDTO[];

  variantProducts?: ProductDataDTO[];
  sizingUrl?: string;

  numberOfBolts?: string;

  hasCalc?: boolean;
  hasCad?: boolean;

  relevantConfigurators?: { code: string }[];

  customerRequestedCADAvailability?: boolean;

  salesCampaigns?: SalesCampaignProduct;

  showPostCartModal?: boolean;

  externalBrand?: ExternalBrand;
  configuratorUrl?: string;
  reconditionable?: boolean;
  additionalCalculationUrl?: string;
  isProductBackInStock?: boolean;
  stockAmount?: number;
  lmbtConfigurator?: string;
  name: string;
}

export interface HousingDTO {
  id: string;
  product: ProductDataDTO;
  designation: string;
  quantity: number;
  type: { code: HousingProductType };
  variantCode: string;
}
interface FeatureValueDTO {
  value: string;
}
export interface FeatureDTO {
  name: string;
  abbreviation: string;
  abbreviationAddition?: string;
  techFilterId: string;
  technicalDescription: string;
  featureUnit: {
    symbol: string;
  } | null;
  featureValues: FeatureValueDTO[] | null;
  type: string;
  isConverted: boolean;
}
export interface ClassificationDTO {
  features: FeatureDTO[];
}

export interface PackagingInformationRefinedEntryDTO {
  code: string;
  packsize: number;
  replenishmentLeadTimeDate?: string;
  primaryWarehouseCode: string;
  primaryWarehouseName: string;
  prefPackQuantity?: number | null;
  aliases?: string[];
  global: WarehouseStorageDataDTO;
  warehouses: WarehouseStorageDataDTO[];
  salesStock?: Record<string, number>;
  minOrderQuantity: number;
}

export interface WarehouseStorageDataDTO {
  code?: string;
  name?: string;
  inStock?: WarehouseAvailabilityEntryDTO | WarehouseAvailabilityEntryDTO[] | null;
  inProduction?: WarehouseAvailabilityEntryDTO[] | null;
  inClarification?: number;
  inProductionWeekly?: WarehouseAvailabilityEntryCWDTO[];
  inStockWeekly?: WarehouseAvailabilityEntryCWDTO | WarehouseAvailabilityEntryCWDTO[];
}

export interface WarehouseAvailabilityEntryCWDTO {
  date?: number;
  cwDate: string;
  amount: number;
}

export interface WarehouseAvailabilityEntryDTO {
  date?: number;
  amount: number;
}

export interface FifteenDigitDTOEntry {
  countryOfOriginList?: string[];
  materialNo: string;
  schaefflerEan?: string;
  customsData?: CustomsDataEntry[];

  packSize?: number;

  // Should only ever contain one entry
  salesStatusData:
    | {
        salesStatus: string;
        salesOrg: string;
      }[]
    | null;

  // Fcc fields
  isFcc?: boolean;
  fccAvailabilityMonths?: number;
  preselect?: boolean;
  rlt?: number;
  fccBreadcrumb?: {
    code: string;
    name: string;
  }[];

  // Phase-out relation fields
  successorPack?: string; // 15-digit materialNo of the successor
  predecessorPacks?: string[]; // 15-digit materialNos of the predecessors
}

export interface CustomsDataEntry {
  customsTariffNumber: string;
  salesOrg: string;
}

export interface TechnicalDetailsDataDTO {
  title: string;
  entries: TechnicalDetailsDataEntryDTO[];
}

export interface TechnicalDetailsDataEntryDTO {
  abbreviationAddition?: string;
  abbreviation: string;
  value: string | string[];
  valueFootnotes: string[];
  unitSymbol: string;
  needsLocalisation?: boolean;
  isConverted: boolean; // True if the value is converted by us instead of coming directly from Schaeffler PIM
  description: string; // if needsLocalisation is true - get from translations, otherwise just use the value ? WTF?
  footnotes: string[];
  groupId: string;
  orderId: string;
  code: string;
  relationalOperator: string;
}
